// src/context/FloatingPanelContext.tsx
// Manages which floating panel (Ember or LoFi) is currently open

import { createContext, useState, useContext, useMemo, useCallback, ReactNode } from 'react';

type PanelType = 'ember' | 'lofi' | null;

interface FloatingPanelContextType {
  openPanel: PanelType;
  setOpenPanel: (panel: PanelType) => void;
}

const FloatingPanelContext = createContext<FloatingPanelContextType | undefined>(undefined);

export function FloatingPanelProvider({ children }: { children: ReactNode }) {
  const [openPanel, setOpenPanel] = useState<PanelType>(null);

  const handleSetOpenPanel = useCallback((panel: PanelType) => {
    setOpenPanel(panel);
  }, []);

  const value = useMemo(() => ({
    openPanel,
    setOpenPanel: handleSetOpenPanel,
  }), [openPanel, handleSetOpenPanel]);

  return (
    <FloatingPanelContext.Provider value={value}>
      {children}
    </FloatingPanelContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFloatingPanel() {
  const context = useContext(FloatingPanelContext);
  if (context === undefined) {
    throw new Error('useFloatingPanel must be used within a FloatingPanelProvider');
  }
  return context;
}
